/**
 * The `#1` chip on an attachment thumbnail — click to rename.
 *
 * Shared by the Notes drawer, the Trade Journal drawer and the Image
 * Board so a label reads and edits the same way wherever it appears.
 * Edit state lives here rather than in the parent: only one chip can
 * be open at a time per thumbnail, and hoisting it just made every
 * caller carry an identical `renamingAttId`.
 *
 * The label exists so a note body can cite a picture ("see #2")
 * without the user having to invent a name for it. The server hands
 * out numbers; renaming is for when a number isn't descriptive
 * enough.
 */
import { useLayoutEffect, useRef, useState } from "react";
import styles from "./AttachmentLabel.module.css";

export function AttachmentLabel({
  label,
  onRename,
  /** Extra hint appended to the chip's tooltip. */
  hint,
  /** Rendered smaller on a dense grid like the Image Board. */
  compact = false,
}: {
  label: string;
  onRename: (next: string) => void;
  hint?: string;
  compact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards the blur handler: committing on Enter also blurs, which
  // would otherwise fire the same rename a second time.
  const committed = useRef(false);

  useLayoutEffect(() => {
    if (!editing) return;
    committed.current = false;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    if (committed.current) return;
    committed.current = true;
    setEditing(false);
    const next = draft.trim();
    // Unchanged, or emptied — the server would just hand back the
    // next free number for a blank, so skip the round trip.
    if (next && next !== label) onRename(next);
  };

  const cancel = () => {
    committed.current = true;
    setEditing(false);
    setDraft(label);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={`${styles.input} ${compact ? styles.compact : ""}`}
        value={draft}
        maxLength={32}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => {
          // Swallow everything: this input sits inside a lightbox and
          // a drawer that both bind arrow keys and Escape globally.
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className={`${styles.chip} ${compact ? styles.compact : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        setDraft(label);
        setEditing(true);
      }}
      title={
        hint ??
        `Image #${label} — click to rename. Cite it as #${label}.`
      }
    >
      #{label}
    </button>
  );
}
